import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	FormLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import SnackbarContext from '../../store/snackbar-context';
import { downloadBase64File } from '../../api/stage3-attestation-api';

/** Limity masy (g) dla formatu listu — FORMAT_GABARYT_EPO, przesyłka polecona (zgodnie z EN). */
const POLECONA_MAX_G_BY_FORMAT: Record<'S' | 'M' | 'L', number> = {
	S: 500,
	M: 1000,
	L: 2000,
};

export type EnShippingExportParams = {
	en_format: 'S' | 'M' | 'L';
	en_masa_kg: number;
	en_kategoria: string;
	en_uwagi?: string;
	/** Plik dla EN: xls (Excel 97–2003) lub xml (IWD — wymaga numerów umowy). */
	en_file_format: 'xls' | 'xml';
	iwd_symbol?: string;
	iwd_karta_umowy?: string;
	iwd_umowa?: string;
};

interface EnShippingDialogProps {
	open: boolean;
	onClose: () => void;
	/** Np. brak tokenu — blokuje akcje, dialog można zamknąć. */
	disabled?: boolean;
	/** Opis kroku 1 (różni się adresatem: uczeń / nauczyciel) — nad polami formatu. */
	step1Description: string;
	/** Opis kroku 2 (m.in. gdzie w ustawieniach jest dopasowanie siatki etykiet). */
	step2Description: string;
	/** Eksport pliku importu EN (XLS / XML) — wywołanie API właściwej zakładki. */
	exportFile: (params: EnShippingExportParams) => Promise<{ data: { filename: string; file_base64: string } }>;
	/** Konwersja PDF etykiet z EN na arkusz A4 105×57 mm — wywołanie API właściwej zakładki. */
	convertLabelsPdf: (fileBase64: string) => Promise<{ data: { filename: string; file_base64: string } }>;
}

/**
 * Dialog „Wysyłka” (Elektroniczny Nadawca): krok 1 — plik importu przesyłek (XLS / IWD XML),
 * krok 2 — konwersja pobranego z EN PDF-a etykiet na arkusz A4 (siatka 105×57 mm).
 * Wydzielony ze Stage3AttestationsTab; używany też w zakładce listów gratulacyjnych nauczycieli.
 */
const EnShippingDialog: React.FC<EnShippingDialogProps> = ({
	open,
	onClose,
	disabled = false,
	step1Description,
	step2Description,
	exportFile,
	convertLabelsPdf,
}) => {
	const { setMsg } = useContext(SnackbarContext);
	const [shippingBusy, setShippingBusy] = useState(false);
	const [enFormat, setEnFormat] = useState<'S' | 'M' | 'L'>('S');
	const [enMasa, setEnMasa] = useState('0.05');
	const [enKategoria, setEnKategoria] = useState('E');
	const [enUwagi, setEnUwagi] = useState('');
	const [enFileFormat, setEnFileFormat] = useState<'xls' | 'xml'>('xls');
	const [iwdSymbol, setIwdSymbol] = useState('');
	const [iwdKartaUmowy, setIwdKartaUmowy] = useState('');
	const [iwdUmowa, setIwdUmowa] = useState('');
	const [labelPdfFileName, setLabelPdfFileName] = useState<string | null>(null);
	const [labelPdfBase64, setLabelPdfBase64] = useState<string | null>(null);
	const [labelConvertBusy, setLabelConvertBusy] = useState(false);

	useEffect(() => {
		if (open) {
			setLabelPdfFileName(null);
			setLabelPdfBase64(null);
		}
	}, [open]);

	const onLabelPdfEnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		e.target.value = '';
		if (!f) {
			setLabelPdfFileName(null);
			setLabelPdfBase64(null);
			return;
		}
		if (!f.name.toLowerCase().endsWith('.pdf')) {
			setMsg({ msg: 'Wybierz plik PDF.', severity: 'error' });
			return;
		}
		if (f.size > 15 * 1024 * 1024) {
			setMsg({ msg: 'Plik za duży (max. ok. 15 MB).', severity: 'error' });
			return;
		}
		setLabelPdfFileName(f.name);
		const reader = new FileReader();
		reader.onload = () => {
			const s = reader.result as string;
			const b64 = s.includes(',') ? s.slice(s.indexOf(',') + 1) : s;
			setLabelPdfBase64(b64 || null);
		};
		reader.readAsDataURL(f);
	};

	const onConvertLabelSheetTo105x57 = async () => {
		if (disabled || !labelPdfBase64) {
			return;
		}
		setLabelConvertBusy(true);
		try {
			const res = await convertLabelsPdf(labelPdfBase64);
			downloadBase64File(res.data.file_base64, res.data.filename, 'application/pdf');
			setMsg({ msg: 'Pobrano PDF z arkuszem etykiet (105×57 mm)', severity: 'info' });
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			setMsg({ msg: err.response?.data?.error ?? 'Błąd konwersji PDF etykiet', severity: 'error' });
		} finally {
			setLabelConvertBusy(false);
		}
	};

	const confirmShippingPostExport = async () => {
		if (disabled) {
			return;
		}
		const masa = parseFloat(enMasa.replace(',', '.'));
		if (Number.isNaN(masa) || masa <= 0) {
			setMsg({ msg: 'Podaj poprawną masę w kilogramach (większą od zera).', severity: 'error' });
			return;
		}
		const maxG = POLECONA_MAX_G_BY_FORMAT[enFormat];
		const g = Math.round(masa * 1000);
		if (g > maxG) {
			setMsg({
				msg: `Niezgodność z limitami masy (FORMAT_GABARYT_EPO): wprowadzono ${g} g, a dla formatu ${enFormat} dopuszczalne jest maks. ${maxG} g (S — max 500 g, M — max 1000 g, L — max 2000 g).`,
				severity: 'error',
			});
			return;
		}
		if (enFileFormat === 'xml') {
			const sym = iwdSymbol.trim();
			const karta = iwdKartaUmowy.trim();
			const um = iwdUmowa.trim();
			if (!sym || !karta || !um) {
				setMsg({
					msg: 'Dla pliku XML (IWD) podaj Symbol, Kartę umowy i Numer umowy — dane z Elektronicznego Nadawcy (zgodnie z umową).',
					severity: 'error',
				});
				return;
			}
		}
		setShippingBusy(true);
		try {
			const res = await exportFile({
				en_format: enFormat,
				en_masa_kg: masa,
				en_kategoria: enKategoria.trim(),
				en_uwagi: enUwagi.trim(),
				en_file_format: enFileFormat,
				...(enFileFormat === 'xml'
					? {
							iwd_symbol: iwdSymbol.trim(),
							iwd_karta_umowy: iwdKartaUmowy.trim(),
							iwd_umowa: iwdUmowa.trim(),
						}
					: {}),
			});
			const mime = enFileFormat === 'xml' ? 'application/xml' : 'application/vnd.ms-excel';
			downloadBase64File(res.data.file_base64, res.data.filename, mime);
			setMsg({
				msg:
					enFileFormat === 'xml'
						? 'Pobrano plik XML dla Elektronicznego Nadawcy'
						: 'Pobrano plik XLS (Excel 97–2003) dla Elektronicznego Nadawcy',
				severity: 'info',
			});
			onClose();
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			setMsg({ msg: err.response?.data?.error ?? 'Błąd generowania pliku dla Poczty Polskiej', severity: 'error' });
		} finally {
			setShippingBusy(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => !shippingBusy && !labelConvertBusy && onClose()}
			fullWidth
			maxWidth='sm'
		>
			<DialogTitle>Wysyłka</DialogTitle>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
				<Typography
					variant='subtitle2'
					component='div'
					sx={{ fontWeight: 600 }}
				>
					1. Elektroniczny Nadawca (import)
				</Typography>
				<Typography
					variant='body2'
					color='text.secondary'
				>
					{step1Description}
				</Typography>
				<FormControl
					fullWidth
					size='small'
				>
					<InputLabel id='ship-en-format-label'>Format listu (S / M / L)</InputLabel>
					<Select<'S' | 'M' | 'L'>
						labelId='ship-en-format-label'
						label='Format listu (S / M / L)'
						value={enFormat}
						onChange={(e: SelectChangeEvent<'S' | 'M' | 'L'>) => setEnFormat(e.target.value as 'S' | 'M' | 'L')}
					>
						<MenuItem value='S'>S (do 500 g)</MenuItem>
						<MenuItem value='M'>M (do 1000 g)</MenuItem>
						<MenuItem value='L'>L (do 2000 g)</MenuItem>
					</Select>
				</FormControl>
				<TextField
					label='Masa (kg)'
					size='small'
					value={enMasa}
					onChange={(e) => setEnMasa(e.target.value)}
					helperText={`Np. 0,05. Dla formatu ${enFormat} maks. ${POLECONA_MAX_G_BY_FORMAT[enFormat]} g (${(POLECONA_MAX_G_BY_FORMAT[enFormat] / 1000).toLocaleString('pl-PL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg) — FORMAT_GABARYT_EPO`}
					fullWidth
				/>
				<FormControl
					fullWidth
					size='small'
				>
					<InputLabel id='ship-en-kat-label'>Kategoria (termin)</InputLabel>
					<Select<string>
						labelId='ship-en-kat-label'
						label='Kategoria (termin)'
						value={enKategoria}
						onChange={(e: SelectChangeEvent<string>) => setEnKategoria(e.target.value)}
					>
						<MenuItem value='E'>E — ekonomiczna</MenuItem>
						<MenuItem value='P'>P — priorytet</MenuItem>
					</Select>
				</FormControl>
				<TextField
					label='Dodatkowy opis przesyłki (opcjonalnie)'
					size='small'
					multiline
					minRows={2}
					value={enUwagi}
					onChange={(e) => setEnUwagi(e.target.value.slice(0, 250))}
					helperText={`${enUwagi.length}/250 — kolumna Uwagi w EN`}
					fullWidth
				/>
				<FormControl
					component='fieldset'
					variant='standard'
				>
					<FormLabel component='legend'>Format pliku dla EN</FormLabel>
					<RadioGroup
						row
						value={enFileFormat}
						onChange={(e) => setEnFileFormat(e.target.value as 'xls' | 'xml')}
					>
						<FormControlLabel
							value='xls'
							control={<Radio size='small' />}
							label='XLS (Excel 97–2003, binarny)'
						/>
						<FormControlLabel
							value='xml'
							control={<Radio size='small' />}
							label='XML (tekstowy)'
						/>
					</RadioGroup>
					<Typography
						variant='caption'
						color='text.secondary'
						display='block'
						sx={{ mt: 0.5 }}
					>
						Import XLS w EN bywa ograniczony do ok. 200 wierszy online — przy większej liczbie rozważ XML lub podział.
						Plik XML musi być zgodny z IWD (wzór i XSD:{' '}
						<a
							href='https://www.poczta-polska.pl/faq/dane/'
							target='_blank'
							rel='noopener noreferrer'
						>
							poczta-polska.pl/faq/dane
						</a>
						).
					</Typography>
				</FormControl>
				{enFileFormat === 'xml' && (
					<>
						<Typography
							variant='subtitle2'
							component='div'
							sx={{ fontWeight: 600 }}
						>
							XML — numery umowy EN (IWD)
						</Typography>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Wartości z umowy / ustawień zbiorów w Elektronicznym Nadawcy (pola Symbol, Karta umowy, Umowa — jak w
							oficjalnym przykładzie PPSAXML).
						</Typography>
						<TextField
							label='Symbol'
							size='small'
							value={iwdSymbol}
							onChange={(e) => setIwdSymbol(e.target.value)}
							fullWidth
							required
						/>
						<TextField
							label='Karta umowy'
							size='small'
							value={iwdKartaUmowy}
							onChange={(e) => setIwdKartaUmowy(e.target.value)}
							fullWidth
							required
						/>
						<TextField
							label='Umowa (numer)'
							size='small'
							value={iwdUmowa}
							onChange={(e) => setIwdUmowa(e.target.value)}
							fullWidth
							required
						/>
					</>
				)}
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
					<Button
						variant='contained'
						onClick={() => void confirmShippingPostExport()}
						disabled={shippingBusy || labelConvertBusy || disabled}
					>
						Pobierz plik dla EN
					</Button>
					{shippingBusy && <CircularProgress size={24} />}
				</Box>
				<Divider />
				<Typography
					variant='subtitle2'
					component='div'
					sx={{ fontWeight: 600 }}
				>
					2. Etykiety z EN → arkusz 105×57 mm
				</Typography>
				<Typography
					variant='body2'
					color='text.secondary'
				>
					{step2Description}
				</Typography>
				<Button
					variant='outlined'
					component='label'
					disabled={disabled || labelConvertBusy}
					sx={{ alignSelf: 'flex-start' }}
				>
					Wybierz PDF z EN
					<input
						type='file'
						hidden
						accept='application/pdf,.pdf'
						onChange={onLabelPdfEnChange}
					/>
				</Button>
				{labelPdfFileName && (
					<Typography
						variant='caption'
						color='text.secondary'
					>
						{labelPdfFileName}
					</Typography>
				)}
				<Button
					variant='contained'
					color='secondary'
					disabled={disabled || !labelPdfBase64 || labelConvertBusy}
					onClick={() => void onConvertLabelSheetTo105x57()}
					sx={{ alignSelf: 'flex-start' }}
				>
					{labelConvertBusy ? (
						<CircularProgress
							size={22}
							color='inherit'
						/>
					) : (
						'Konwertuj na arkusz A4 (105×57 mm)'
					)}
				</Button>
				{labelConvertBusy && <CircularProgress size={24} />}
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onClose}
					disabled={shippingBusy || labelConvertBusy}
				>
					Zamknij
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EnShippingDialog;
